import * as Sentry from '@sentry/react'
import { formatBuildIdentity } from './build-info'

const BUGSINK_DSN =
  'https://da0483b57ded48bcbcb4df7d8ec49607@bugsink.falbesoner.dev/1'

/**
 * HTTP status codes to ignore (transient/expected errors).
 * - 401: Unauthorized (session expired, handled by app)
 * - 403: Forbidden (permission denied, expected for restricted actions)
 * - 408: Request timeout
 * - 502/503/504: Gateway/service temporarily unavailable (deployments, restarts)
 */
const IGNORED_STATUS_CODES = [401, 403, 408, 502, 503, 504]

/**
 * Error messages that indicate network issues (not bugs).
 */
const NETWORK_ERROR_PATTERNS = [
  'Failed to fetch',
  'NetworkError',
  'net::ERR_',
  'ECONNREFUSED',
  'ETIMEDOUT',
  'Load failed',
]

interface MonitoringOptions {
  app: string
  /**
   * Release version. Defaults to the build identity injected by `@kostbar/monitoring/vite`, so an
   * app that uses the plugin gets per-release error grouping without passing anything.
   */
  version?: string
  environment?: string
  enabled?: boolean
  /** Additional status codes to ignore */
  ignoredStatusCodes?: number[]
}

/**
 * Initialize error monitoring with Bugsink (Sentry-compatible).
 * Call this before rendering your React app.
 */
export function initMonitoring(options: MonitoringOptions) {
  const {
    app,
    version = formatBuildIdentity(),
    environment,
    enabled = true,
    ignoredStatusCodes = [],
  } = options

  if (!enabled) return

  const allIgnoredCodes = [...IGNORED_STATUS_CODES, ...ignoredStatusCodes]

  Sentry.init({
    dsn: BUGSINK_DSN,
    release: version ? `${app}@${version}` : app,
    environment: environment ?? 'development',

    // Bugsink recommendations
    sendDefaultPii: true, // Self-hosted, so PII is OK
    tracesSampleRate: 0, // Bugsink doesn't support traces

    // Disable session tracking (not supported by Bugsink)
    integrations: (integrations) =>
      integrations.filter((i) => i.name !== 'BrowserSession'),

    // Filter out transient/network errors
    beforeSend(event, hint) {
      const error = hint.originalException

      // Check for network errors by message
      if (error instanceof Error) {
        const isNetworkError = NETWORK_ERROR_PATTERNS.some((pattern) =>
          error.message.includes(pattern)
        )
        if (isNetworkError) {
          console.debug('[monitoring] Ignoring network error:', error.message)
          return null
        }
      }

      // Check for ignored HTTP status codes (from tags)
      const statusTag = event.tags?.api_status
      if (statusTag) {
        const status = Number(statusTag)
        if (allIgnoredCodes.includes(status)) {
          console.debug('[monitoring] Ignoring transient API error:', status)
          return null
        }
      }

      return event
    },
  })
}

// Re-export Sentry utilities for direct use
export const captureException = Sentry.captureException
export const captureMessage = Sentry.captureMessage
export const setUser = Sentry.setUser
export const setTag = Sentry.setTag
export const setContext = Sentry.setContext

// Re-export ErrorBoundary for React
export { ErrorBoundary } from '@sentry/react'

/**
 * Test function to verify Bugsink integration.
 * Call from browser console: window.testBugsink()
 */
export function testBugsink(message = 'Test error from frontend') {
  const error = new Error(message)
  Sentry.captureException(error)
  console.log('[monitoring] Test error sent to Bugsink:', message)
  return 'Check Bugsink dashboard for this error'
}

// Expose test function globally in dev mode
if (typeof window !== 'undefined') {
  ;(window as unknown as { testBugsink: typeof testBugsink }).testBugsink =
    testBugsink
}

// Build identity of this bundle, for About screens and version displays.
export { getBuildInfo, formatBuildIdentity } from './build-info'
export type { BuildInfo } from './build-info'
